<template>
	<Teleport to="#teleports" :disabled="!isClient">
		<TransitionGroup name="dropdown">
			<div v-if="isVisible && isClient && !noBackdrop" class="dropdown-background" :class="{ 'dropdown-background--no-blur': noBlur, 'dropdown-background--pass-through': passThrough }" />
			<div v-if="isVisible && isClient" ref="dropdownRef" class="dropdown-menu" :class="{ 'light-mode': invert, 'dropdown-menu--pass-through': passThrough }" :style="dropdownStyles" role="menu" aria-orientation="vertical" @click="handleMenuClick">
				<slot />
			</div>
		</TransitionGroup>
	</Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";

const props = defineProps({
	modelValue: {
		type: Boolean,
		default: false,
	},
	target: {
		type: [Object, String, null],
		required: true,
		validator: (value) => {
			if (value === null) return true;
			if (typeof value === "string") return true;
			if (typeof value === "object") return true;
			if (typeof window !== "undefined" && value instanceof Element) return true;
			return false;
		},
	},
	align: {
		type: String,
		default: "left",
		validator: (value) => ["left", "right", "center"].includes(value),
	},
	position: {
		type: String,
		default: "bottom",
		validator: (value) => ["bottom", "top"].includes(value),
	},
	gap: {
		type: Number,
		default: 8,
	},
	offset: {
		type: Number,
		default: 0,
	},
	closeOnOutsideClick: {
		type: Boolean,
		default: true,
	},
	closeOnEscape: {
		type: Boolean,
		default: true,
	},
	closeOnItemClick: {
		type: Boolean,
		default: true,
	},
	minWidth: {
		type: Number,
		default: 200,
	},
	maxWidth: {
		type: Number,
		default: 400,
	},
	maxHeight: {
		type: Number,
		default: null,
	},
	invert: {
		type: Boolean,
		default: false,
	},
	noBackdrop: {
		type: Boolean,
		default: false,
	},
	noBlur: {
		type: Boolean,
		default: false,
	},
	passThrough: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits(["update:modelValue", "close"]);

const isClient = ref(false);
const dropdownRef = ref(null);
const dropdownStyles = ref({});
const targetElement = ref(null);
const resizeObserver = ref(null);
const intersectionObserver = ref(null);

const isVisible = computed({
	get: () => props.modelValue,
	set: (value) => emit("update:modelValue", value),
});

const getTargetElement = () => {
	if (!isClient.value) return null;
	if (!props.target) {
		return null;
	}

	// If it's already a DOM element
	if (props.target instanceof Element) {
		return props.target;
	}

	// If it's a Vue ref with a DOM element
	if (props.target && typeof props.target === "object" && "value" in props.target) {
		if (props.target.value instanceof Element) {
			return props.target.value;
		}
		// Sometimes the value itself might be what we need
		if (props.target.value) {
			return props.target.value;
		}
	}

	// If it's a selector string
	if (typeof props.target === "string") {
		try {
			const element = document.querySelector(props.target);
			return element;
		} catch (error) {
			return null;
		}
	}

	return null;
};

const calculatePosition = async () => {
	if (!isClient.value) {
		return;
	}

	// Wait for next tick to ensure DOM is updated
	await nextTick();

	const targetEl = getTargetElement();
	if (!targetEl) {
		return;
	}

	// Update the stored reference
	targetElement.value = targetEl;

	try {
		const targetRect = targetEl.getBoundingClientRect();

		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		const edgePadding = 10;

		// Use estimated dimensions first
		let dropdownWidth = props.minWidth;
		let dropdownHeight = 200;

		// If dropdown is already rendered, use actual dimensions
		// Use offsetWidth/offsetHeight to get layout size unaffected by CSS transforms (e.g. scale during entry animation)
		if (dropdownRef.value) {
			dropdownWidth = dropdownRef.value.offsetWidth || dropdownWidth;
			dropdownHeight = dropdownRef.value.offsetHeight || dropdownHeight;
		}

		let top, left;

		// Calculate available space above and below the target
		const spaceBelow = viewportHeight - targetRect.bottom - props.gap - edgePadding;
		const spaceAbove = targetRect.top - props.gap - edgePadding;

		// Determine placement: prefer the requested position, but place where there's more room
		let placementBelow;
		if (props.position === "bottom") {
			placementBelow = dropdownHeight <= spaceBelow || spaceBelow >= spaceAbove;
		} else {
			placementBelow = dropdownHeight > spaceAbove && spaceBelow > spaceAbove;
		}

		let availableHeight = placementBelow ? spaceBelow : spaceAbove;
		if (props.maxHeight) {
			availableHeight = Math.min(availableHeight, props.maxHeight);
		}
		const maxHeight = Math.max(availableHeight, 80);

		if (placementBelow) {
			top = targetRect.bottom + props.gap;
		} else {
			const usedHeight = Math.min(dropdownHeight, maxHeight);
			top = targetRect.top - usedHeight - props.gap;
		}

		// Calculate horizontal position based on alignment
		switch (props.align) {
			case "left":
				left = targetRect.left + props.offset;
				break;
			case "right":
				left = targetRect.right - dropdownWidth - props.offset;
				break;
			case "center":
				left = targetRect.left + targetRect.width / 2 - dropdownWidth / 2 + props.offset;
				break;
		}

		// Ensure dropdown stays within viewport horizontally
		if (left < edgePadding) {
			left = edgePadding;
		} else if (left + dropdownWidth > viewportWidth - edgePadding) {
			left = viewportWidth - dropdownWidth - edgePadding;
		}

		const newStyles = {
			position: "fixed",
			top: `${top}px`,
			left: `${left}px`,
			minWidth: `${props.minWidth}px`,
			maxWidth: `${props.maxWidth}px`,
			maxHeight: `${maxHeight}px`,
			zIndex: 1000,
		};

		dropdownStyles.value = newStyles;
	} catch (error) {
		console.error("Dropdown: Error calculating position:", error);
	}
};

const setupTargetObservation = () => {
	if (!isClient.value) return;

	cleanupObservers();

	const targetEl = getTargetElement();
	if (!targetEl) return;

	targetElement.value = targetEl;

	// Initial position calculation
	calculatePosition();

	// Set up ResizeObserver for target element size changes
	if (typeof window !== "undefined" && window.ResizeObserver) {
		try {
			resizeObserver.value = new ResizeObserver(() => {
				if (isVisible.value) {
					calculatePosition();
				}
			});
			resizeObserver.value.observe(targetEl);
		} catch (error) {
			console.warn("Error setting up ResizeObserver:", error);
		}
	}

	// Set up IntersectionObserver for target element position changes
	if (typeof window !== "undefined" && window.IntersectionObserver) {
		try {
			intersectionObserver.value = new IntersectionObserver(
				() => {
					if (isVisible.value) {
						calculatePosition();
					}
				},
				{
					root: null,
					threshold: [0, 0.1, 0.5, 0.9, 1],
				},
			);
			intersectionObserver.value.observe(targetEl);
		} catch (error) {
			console.warn("Error setting up IntersectionObserver:", error);
		}
	}
};

const cleanupObservers = () => {
	if (resizeObserver.value) {
		try {
			resizeObserver.value.disconnect();
		} catch (error) {
			console.warn("Error disconnecting ResizeObserver:", error);
		}
		resizeObserver.value = null;
	}

	if (intersectionObserver.value) {
		try {
			intersectionObserver.value.disconnect();
		} catch (error) {
			console.warn("Error disconnecting IntersectionObserver:", error);
		}
		intersectionObserver.value = null;
	}
};

const closeDropdown = () => {
	isVisible.value = false;
	emit("close");
};

const handleMenuClick = (event) => {
	if (!props.closeOnItemClick || !isClient.value) return;

	const clickedElement = event.target;
	const liElement = clickedElement.closest("li");

	if (liElement && (clickedElement.tagName === "BUTTON" || clickedElement.tagName === "A")) {
		closeDropdown();
	}
};

const handleClickOutside = (event) => {
	if (!props.closeOnOutsideClick || !isClient.value || !isVisible.value) return;

	const targetEl = targetElement.value;
	const dropdownEl = dropdownRef.value;

	if (dropdownEl && !dropdownEl.contains(event.target) && targetEl && !targetEl.contains(event.target)) {
		closeDropdown();
	}
};

const handleEscapeKey = (event) => {
	if (!isClient.value) return;
	if (props.closeOnEscape && event.key === "Escape" && isVisible.value) {
		closeDropdown();
	}
};

const handleResize = () => {
	if (isClient.value && isVisible.value) {
		calculatePosition();
	}
};

const handleScroll = () => {
	if (isClient.value && isVisible.value) {
		calculatePosition();
	}
};

// Watch for target changes
watch(
	() => props.target,
	(newTarget) => {
		if (!isClient.value || !newTarget) return;
		setupTargetObservation();
	},
	{ immediate: false },
);

// Also watch the target's value if it's a ref object
watch(
	() => {
		if (props.target && typeof props.target === "object" && "value" in props.target) {
			return props.target.value;
		}
		return props.target;
	},
	(newValue) => {
		if (!isClient.value || !newValue) return;
		if (isVisible.value) {
			calculatePosition();
		}
	},
	{ immediate: false },
);

// Watch for positioning prop changes
watch([() => props.align, () => props.position, () => props.gap, () => props.offset], () => {
	if (isClient.value && isVisible.value) {
		calculatePosition();
	}
});

// Recalculate position when dropdown becomes visible
watch(isVisible, async (newValue, oldValue) => {
	if (!isClient.value) {
		return;
	}

	if (newValue) {
		// Set up observation for the target
		setupTargetObservation();

		// Calculate initial position (dimensions may be estimated at this point)
		await nextTick();
		await calculatePosition();

		// Hide while we wait for the dropdown to paint so we can measure real dimensions
		dropdownStyles.value = { ...dropdownStyles.value, visibility: 'hidden' };

		// Fine-tune after a frame to ensure dropdown is rendered with real dimensions
		requestAnimationFrame(async () => {
			await calculatePosition();
			// Reveal with correct position — animation plays from here
			dropdownStyles.value = { ...dropdownStyles.value, visibility: '' };
		});
	} else {
		cleanupObservers();
	}
});

onMounted(() => {
	isClient.value = true;

	// Set up global event listeners
	if (typeof document !== "undefined") {
		document.addEventListener("click", handleClickOutside, true);
		document.addEventListener("keydown", handleEscapeKey);
	}

	if (typeof window !== "undefined") {
		window.addEventListener("resize", handleResize);
		window.addEventListener("scroll", handleScroll, true);
	}
});

onUnmounted(() => {
	cleanupObservers();

	if (typeof document !== "undefined") {
		document.removeEventListener("click", handleClickOutside, true);
		document.removeEventListener("keydown", handleEscapeKey);
	}

	if (typeof window !== "undefined") {
		window.removeEventListener("resize", handleResize);
		window.removeEventListener("scroll", handleScroll, true);
	}
});
</script>

<style lang="scss" scoped>
.dropdown-background {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	z-index: 999;
	backdrop-filter: blur(1px);
	transition: backdrop-filter 0.35s cubic-bezier(0.075, 0.82, 0.165, 1);

	&--no-blur {
		backdrop-filter: none;
	}

	&--pass-through {
		pointer-events: none;
	}
}

.dropdown-menu {
	background: var(--background-75);
	border: 1px solid var(--background-200);
	border-radius: 8px;
	box-shadow:
		0 10px 15px -3px rgba(0, 0, 0, 0.1),
		0 4px 6px -2px rgba(0, 0, 0, 0.05);
	overflow: hidden;
	max-height: 400px;
	overflow-y: auto;

	&--pass-through {
		pointer-events: none;
	}
}

:deep(ul) {
	list-style: none;
	margin: 0;
	padding: 0;
	overflow: hidden;
	background-color: var(--background-75);
}

:deep(li) {
	border-bottom: 1px solid var(--background-200);
	max-height: 44px;
	border-radius: 0px;
}

:deep(li:last-child) {
	border-bottom: none;
}

:deep(li button),
:deep(li a) {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.8rem 1rem;
	height: 100%;
	max-height: 44px;
	border: none;
	background: none;
	text-align: left;
	cursor: pointer;
	touch-action: manipulation;
	transition: background-color 0.2s ease;
	color: var(--text-200);
	background-color: var(--background-75);
	font-size: 14px;
	text-decoration: none;
}

:deep(li a) {
	width: auto;
}

:deep(li button .image-wrapper),
:deep(li a .image-wrapper) {
	width: 16px;
	max-width: 16px;
	height: 16px;
	max-height: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
}

:deep(li button:hover),
:deep(li a:hover) {
	background-color: var(--background-100);
}

:deep(li button:focus),
:deep(li a:focus) {
	background-color: var(--background-200);
	outline: none;
}

:deep(li button:disabled) {
	opacity: 0.5;
	cursor: not-allowed;
}

:deep(li button:disabled:hover) {
	background-color: transparent;
}

:deep(li button .e-icon),
:deep(li a .e-icon) {
	font-size: 16px;
	width: 18px;
	height: 18px;
	text-align: center;
	flex-shrink: 0;
}

:deep(.dropdown-divider) {
	height: 1px;
	margin: 4px 0;
}

:deep(.dropdown-header) {
	padding: 8px 16px;
	font-size: 11px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	background-color: var(--background-100);
	border-bottom: 1px solid var(--background-200);

	span {
		color: var(--text-500);
	}
}

:deep(.e-button) {
	width: 100%;
	border-radius: 0;
	justify-content: flex-start;

	span {
		justify-content: flex-start;
	}
}

.dropdown-enter-active,
.dropdown-leave-active {
	transition: all 0.2s ease;
	transform-origin: top;
}

.dropdown-enter-from {
	opacity: 0;
	transform: scale(0.95) translateY(-8px);
}

.dropdown-leave-to {
	opacity: 0;
	transform: scale(0.95) translateY(-8px);
}

// @media (prefers-color-scheme: light) {
// 	:deep(ul) {
// 		background-color: var(--background-25);
// 	}
// 	:deep(li button),
// 	:deep(li a) {
// 		background-color: var(--background-25);
// 	}

// 	:deep(li button:hover),
// 	:deep(li a:hover) {
// 		background-color: var(--background-75);
// 	}

// 	:deep(.dropdown-header) {
// 		border-bottom: 1px solid var(--background-200);
// 		background-color: var(--background-75);
// 	}
// }

// @media (prefers-color-scheme: dark) {
// 	:deep(ul) {
// 		background-color: var(--background-75);
// 	}
// 	:deep(li button),
// 	:deep(li a) {
// 		background-color: var(--background-75);
// 	}

// 	:deep(li button:hover),
// 	:deep(li a:hover) {
// 		background-color: var(--background-200);
// 	}

// 	:deep(.dropdown-header) {
// 		border-bottom: 1px solid var(--background-200);
// 		background-color: var(--background-100);
// 	}
// }

:root[data-theme="light"] {
	:deep(li button),
	:deep(li a) {
		background-color: var(--background-25);
	}

	:deep(li button:hover),
	:deep(li a:hover) {
		background-color: var(--background-50);
	}

	:deep(.dropdown-header) {
		border-bottom: 1px solid var(--background-200);
		background-color: var(--background-100);
	}
}

:root[data-theme="dark"] {
	:deep(ul) {
		background-color: var(--background-75);
	}
	:deep(li button),
	:deep(li a) {
		background-color: var(--background-75);
	}

	:deep(li button:hover),
	:deep(li a:hover) {
		background-color: var(--background-200);
	}

	:deep(.dropdown-header) {
		border-bottom: 1px solid var(--background-200);
		background-color: var(--background-100);
	}
}
</style>
